import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Avatar from '@material-ui/core/Avatar';
import Link from '@material-ui/core/Link';
import { CircularProgress, makeStyles } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { Context as AuthContext } from '../../../contexts/authContext';
import { MDBox } from '../../MDBox';
import { MDInput } from '../../MDInput';
import { MDTypography } from '../../MDTypography';
import { MDButton } from '../../MDButton';
import bgImage from '../../../assets/images/illustrations/illustration-reset.jpg';
import { IllustrationLayout } from '../../layout/IllustrationLayout';

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(8, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: 'black',
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(4),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(2, 0),
  },
  codeInput: {
    display: 'flex',
    paddingBottom: theme.spacing(6),
    alignContent: 'center',
    justifyContent: 'space-between',
    maxWidth: '290px',
    margin: '0 auto',
  },
  codeBox: {
    width: '40px',
    height: '40px',
    fontSize: '24px',
    textAlign: 'center',
    marginRight: theme.spacing(1),
  },
}));

export const Login2FA = () => {
  const [code, setCode] = useState(new Array(6).fill(''));
  const inputs = useRef<any>([]);
  const location = useLocation<{ email: string; password: string }>();
  const { email, password } = location.state || { email: '', password: '' };
  const { signIn, errors, isLoading, clearState } = useContext(AuthContext);
  const classes = useStyles();

  useEffect(() => {
    if (clearState) {
      clearState();
    }
  }, []);

  const onSignIn = async () => {
    if (signIn) {
      await signIn(email, password, code.join(''));
    }
  };

  const handleCodeChange = (
    index: number,
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (/^[0-9]$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (index < 5) {
        inputs?.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (e.key === 'Backspace') {
      const newCode = [...code];
      newCode[index] = '';
      setCode(newCode);

      if (index > 0) {
        inputs?.current[index - 1]?.focus();
      }
    }
  };

  return (
    <IllustrationLayout
      title="2FA Authentication"
      description="Please enter the 2FA code sent to your"
      illustration={bgImage}
    >
      {errors && errors.serverError && (
        <MDTypography color="error">
          Failed, Please check user name and password and try again
        </MDTypography>
      )}
      <MDBox mb={2} display="flex" justifyContent="center">
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon color="secondary" />
        </Avatar>
      </MDBox>
      <MDBox component="form" role="form">
        <MDBox className={classes.codeInput}>
          {[0, 1, 2, 3, 4, 5].map((_, index) => (
            <MDInput
              key={index}
              className={classes.codeBox}
              variant="outlined"
              inputProps={{
                maxLength: 1,
                style: { textAlign: 'center' },
                'aria-label': `Digit ${index + 1}`,
              }}
              value={code[index]}
              onChange={(e) => handleCodeChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              ref={(el) => (inputs.current[index] = el)}
            />
          ))}
        </MDBox>
        <MDButton
          fullWidth
          variant="gradient"
          color="info"
          onClick={onSignIn}
          disabled={isLoading}
        >
          {!isLoading ? 'Log in' : <CircularProgress color="inherit" />}
        </MDButton>
        <MDBox mt={5}>
          <MDTypography variant="body2" color="info" align="center">
            {'Copyright © '}
            <Link color="inherit" href="https://www.fairfare.co/">
              FairFare
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
          </MDTypography>
        </MDBox>
      </MDBox>
    </IllustrationLayout>
  );
};
